import React from "react";
import { Typography, Box } from "@mui/material";

const PatternBasicsGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        Written vs. Chart Formats
      </Typography>
      <Typography variant="body1">
        Knitting patterns come in two main formats: written instructions and charts. Many patterns
        include both, letting you choose which format you prefer.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Written Patterns
      </Typography>
      <Typography variant="body1">
        Written patterns use text and abbreviations to describe what to do:
      </Typography>
      <Box
        component="pre"
        className="pattern-example"
        sx={{
          fontFamily: "Roboto Mono, monospace",
          backgroundColor: "var(--lp-cream)",
          padding: "1rem",
          borderRadius: "8px",
        }}
      >
        {`Row 1: *K2, P2; rep from * to end.
Row 2: *P2, K2; rep from * to end.
Rep Rows 1-2 for pattern.`}
      </Box>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Pros</span>
          <span className="feature-item-description">
            Familiar format, easy to follow step-by-step, works well for simple patterns, portable
            (can read from phone/tablet)
          </span>
        </li>
        <li>
          <span className="feature-item-title">Cons</span>
          <span className="feature-item-description">
            Can be lengthy for complex patterns, harder to visualize the final design, easy to lose
            your place in long instructions
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Chart Patterns
      </Typography>
      <Typography variant="body1">
        Chart patterns use a grid with symbols to show stitch placement:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Pros</span>
          <span className="feature-item-description">
            Visual representation, compact for complex patterns, easier to see pattern structure,
            international (symbols work across languages)
          </span>
        </li>
        <li>
          <span className="feature-item-title">Cons</span>
          <span className="feature-item-description">
            Learning curve if you're new to charts, requires understanding symbols, can be harder to
            read on small screens
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Choosing the Right Format
      </Typography>
      <Typography variant="body1">
        Consider these factors when choosing which format to use:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Your Experience</span>
          <span className="feature-item-description">
            If you're comfortable with charts, they're often faster for complex patterns. If you're
            new to charts, start with written patterns or patterns that include both formats.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Pattern Complexity</span>
          <span className="feature-item-description">
            Simple patterns (like basic ribbing) are often easier in written form. Complex patterns
            (like lace or cables) are usually clearer as charts.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Your Preference</span>
          <span className="feature-item-description">
            Some knitters are 'chart people' and some are 'written pattern people.' There's no right
            or wrong—use what works for you!
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Patterns with Both Formats
      </Typography>
      <Typography variant="body1">
        Many modern patterns include both written instructions and charts. This is ideal because:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Flexibility</span>
          <span className="feature-item-description">
            You can switch between formats as needed. Use the chart to see the big picture, then
            refer to written instructions for specific rows.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Learning</span>
          <span className="feature-item-description">
            Having both formats helps you learn to read charts by comparing them to written
            instructions.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Verification</span>
          <span className="feature-item-description">
            You can double-check your work by comparing what you're knitting to both the chart and
            written instructions.
          </span>
        </li>
      </ul>
      <Typography variant="body2" color="text.secondary">
        Loops & Purls automatically generates written instructions from your chart, giving you both
        formats in one tool!
      </Typography>
    </>
  );
};

export default PatternBasicsGuide;

